import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon, XIcon } from 'lucide-react';
import { useFunnel } from '../../contexts/FunnelContext';
import { formatMoney, getBundle, priceBundle } from '../../utils/pricing';
import { orderTotals } from '../../utils/checkout';
import type { OrderLine } from '../../utils/checkout';
const m = (n: number) => formatMoney(n, 'en');

/** Live "Your MarineForceLED System" summary — exactly what goes to Shopify. */
export function OrderSummary() {
  const {
    state,
    order,
    setAddOnQty
  } = useFunnel();
  if (!state) return null;
  const t = orderTotals(order);
  return <section aria-labelledby="order-summary-title" data-id="element-85" data-mp="2wp3l1twj4w3n">
      <h3 id="order-summary-title" className="font-display text-xl font-semibold uppercase tracking-[0.06em] text-white" data-id="element-86" data-mp="31mvmpfmz7wl1">
        Your MarineForceLED System
      </h3>
      <ul className="mt-3 divide-y divide-white/10 border-y border-white/10" data-id="element-87" data-mp="35qa5ge2aur26">
        <SummaryRow line={state.primary} label="Selected system" data-id="element-88" data-mp="3aobq33fdiyfr" />
        {state.addOns.map(l => <SummaryRow key={l.bundleId} line={l} label="Added system" onRemove={() => setAddOnQty(l.bundleId, 0)} data-id="element-89" data-mp="2vdos63th35a5" />)}
      </ul>
      <dl className="mt-4 space-y-1.5 text-sm" data-id="element-90" data-mp="2rcwe8x9noc7j">
        <Row label="M1 PRO lights" value={String(t.lights)} data-id="element-91" data-mp="2mciahrf37ofb" />
        <Row label="Magnetic mounting bases" value={String(t.lights)} data-id="element-92" data-mp="2hcm27fzcxaty" />
        {t.savings > 0 && <Row label="Savings vs. individual lights" value={`−${m(t.savings)}`} accent data-id="element-93" data-mp="3f04lhkqn9vc7" />}
      </dl>
      <div className="mt-4 flex items-baseline justify-between border-t border-white/10 pt-4" data-id="element-94" data-mp="3a11g1z6r5n3p">
        <span className="text-titanium" data-id="element-95" data-mp="3a3dy0ccifxgr">System subtotal</span>
        <span className="font-display text-4xl font-semibold leading-none text-white" aria-live="polite" data-id="element-96" data-mp="358qfqv83nzam">
          {m(t.total)}
        </span>
      </div>
      <p className="mt-2 text-xs text-titanium/80" data-id="element-97" data-mp="1ky9xxvag6hh">
        Shipping and taxes calculated at secure Shopify checkout.{' '}
        <Link to="/warranty" className="underline decoration-white/20 underline-offset-4 hover:text-white" data-id="element-98" data-mp="3syuil0dgrv0k">
          Warranty details
        </Link>
      </p>
    </section>;
}
function SummaryRow({
  line,
  label,
  onRemove
}: {
  line: OrderLine;
  label: string;
  onRemove?: () => void;
}) {
  const b = getBundle(line.bundleId);
  const total = Math.round(priceBundle(b).total * line.qty * 100) / 100;
  return <li className="flex items-center gap-3 py-3" data-id="element-99" data-mp="1mac1psfwjrzb">
      <img src={b.image} alt="" className="h-12 w-10 shrink-0 object-cover object-[50%_30%]" data-id="element-100" data-mp="17ecm7wqgqpk5" />
      <div className="min-w-0 flex-1" data-id="element-101" data-mp="1cdfqheymcrr2">
        <p className="text-[11px] uppercase tracking-[0.14em] text-titanium/80" data-id="element-102" data-mp="1ceiftigamm4v">{label}</p>
        <p className="truncate text-sm font-semibold text-white" data-id="element-103" data-mp="yeg3scuo2mxj">
          {line.qty > 1 ? `${line.qty} × ` : ''}
          {b.tagline} · {b.qty * line.qty} {b.qty * line.qty === 1 ? 'light' : 'lights'}
        </p>
        <p className="flex items-center gap-1 text-[12px] text-titanium" data-id="element-104" data-mp="2st0im8wb3o90">
          <ShieldCheckIcon className={`h-3.5 w-3.5 ${b.warranty === 'lifetime' ? 'text-marine' : ''}`} aria-hidden data-id="element-105" data-mp="2xrbrohes1uq6" />
          {b.warranty === 'lifetime' ? 'Limited Lifetime Warranty' : '1-Year Limited Warranty'}
        </p>
      </div>
      <span className="text-sm font-semibold text-white" data-id="element-106" data-mp="32x75r6xcwk3f">{m(total)}</span>
      {onRemove && <button type="button" onClick={onRemove} aria-label={`Remove ${b.tagline}`} className="flex h-10 w-10 shrink-0 items-center justify-center text-titanium transition-colors duration-150 hover:text-white" data-id="element-107" data-mp="3golp5ev6enb0">
          <XIcon className="h-4 w-4" aria-hidden data-id="element-108" data-mp="3lmn7fxks2ilf" />
        </button>}
    </li>;
}
function Row({
  label,
  value,
  accent
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return <div className="flex justify-between gap-4" data-id="element-109" data-mp="3vkky0rq40rh7">
      <dt className="text-titanium" data-id="element-110" data-mp="3vktc2mxyy2tz">{label}</dt>
      <dd className={accent ? 'font-semibold text-ice' : 'text-white'} data-id="element-111" data-mp="257m1wxnoqynu">{value}</dd>
    </div>;
}